import type { Point2D, WorldRect } from "../types";

export type WorldExtent = Point2D | WorldRect;

const isFinitePoint = (extent: Point2D): boolean =>
    Number.isFinite(extent.x) && Number.isFinite(extent.y);

const isFiniteRect = (extent: WorldRect): boolean =>
    isFinitePoint(extent) &&
    Number.isFinite(extent.width) &&
    Number.isFinite(extent.height) &&
    extent.width >= 0 &&
    extent.height >= 0;

/** Combines world-space points and non-negative rectangles into one bounds rectangle. */
export function combineWorldExtents(
    extents: readonly WorldExtent[],
): WorldRect | null {
    let xMin = Infinity;
    let xMax = -Infinity;
    let yMin = Infinity;
    let yMax = -Infinity;

    for (const extent of extents) {
        const isRectangle = "width" in extent || "height" in extent;
        if (isRectangle) {
            if (!isFiniteRect(extent as WorldRect)) continue;
            const right = extent.x + extent.width;
            const bottom = extent.y + extent.height;
            if (!Number.isFinite(right) || !Number.isFinite(bottom)) continue;

            xMin = Math.min(xMin, extent.x);
            xMax = Math.max(xMax, right);
            yMin = Math.min(yMin, extent.y);
            yMax = Math.max(yMax, bottom);
            continue;
        }

        if (!isFinitePoint(extent)) continue;

        xMin = Math.min(xMin, extent.x);
        xMax = Math.max(xMax, extent.x);
        yMin = Math.min(yMin, extent.y);
        yMax = Math.max(yMax, extent.y);
    }

    if (![xMin, xMax, yMin, yMax].every(Number.isFinite)) return null;

    const width = xMax - xMin;
    const height = yMax - yMin;
    if (!Number.isFinite(width) || !Number.isFinite(height)) return null;

    return { x: xMin, y: yMin, width, height };
}
